import { useEffect, useMemo, useState } from "react";
import { Checkbox, Spinner } from "@chakra-ui/react";
import { FiArrowRight, FiSearch, FiX } from "react-icons/fi";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { CompactTextInput } from "newui/components/ui/InputWithLabel";
import { getUserAssignmentsPage } from "newui/features/RolesPermissions/api/rolesPermissionsApi";
import type {
	UserAssignment,
	UserPage,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface BulkUserPickerModalProps {
	isOpen: boolean;
	onClose: () => void;
	/**
	 * Hand-off to the Add flow in multi-user mode. This modal deliberately does not
	 * know what happens next — it yields who was chosen and stops.
	 *
	 * `selectedUsers` carries the SAME users as `selectedUserIds`, in the same
	 * order, as whole records. Ids alone forced the confirm dialog downstream to
	 * render "User #1088" for its per-user headers, because by then the only thing
	 * that knew these people's names — this component — had thrown them away
	 * (gap review F5). The second parameter is additive: a consumer that only
	 * declares the first keeps working untouched.
	 */
	onNext: (selectedUserIds: number[], selectedUsers: UserAssignment[]) => void;
}

// CONTRACT §7 (C2) — this caller, and only this caller, pages /rbac/users.
export const BULK_USER_PICKER_PAGE_SIZE = 25;
export const BULK_USER_PICKER_KEY = "rbac-user-assignments-page";
/**
 * Long enough that ordinary typing produces one request rather than one per
 * keystroke, short enough that the list still feels live.
 */
export const BULK_USER_PICKER_SEARCH_DEBOUNCE_MS = 300;

/**
 * How a person is named on screen.
 *
 * There is no single app-wide convention (see the note in this module's tests):
 * `User.type.ts` carries `full_name`, the RFx panel types carry `full_name` on a
 * nested `user`, and `MemberActionsMenu` uses `member.name || member.email`. The
 * RBAC surface has exactly one: `UserAssignment.name`, which is what
 * `/rbac/users` returns and what every RBAC screen already renders. This follows
 * that, and borrows `MemberActionsMenu`'s email fallback for the case where a
 * record has no name yet (an invited, never-completed user).
 */
export const userDisplayName = (user: UserAssignment): string =>
	(user.name ?? "").trim() || user.email;

/**
 * PRO-757 A9 — takes an ALREADY-RESOLVED display name (i.e. the output of `userDisplayName`),
 * never `user.name` raw. `UserAssignment.name` is typed `string` but is genuinely `null` for an
 * invited, never-completed user (FOLLOW-UPS #7), and `null.split(" ")` threw inside render and
 * took the entire picker down — a blank modal, no error, for a perfectly ordinary company.
 */
const initials = (displayName: string): string =>
	displayName
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

/**
 * PRO-757 (D9-T1) — pick the users first, then grant access to all of them in
 * one pass (spec C2). The searchable checkbox list + chips UI is lifted from the
 * retired bulk-assign wizard's user-picking step; the mode toggle and the
 * template picker are NOT lifted — both are retired (ticket line 12, R5).
 *
 * Two things differ from the picker it replaces, and both are deliberate:
 *
 * 1. **Paging is server-side.** The old picker held every user in memory and
 *    sliced the array. This one asks for 25 at a time and APPENDS each page, so
 *    a company with hundreds of users costs one small request to open.
 * 2. **Selection is held as user OBJECTS, not ids or list indices.** A selected
 *    user must keep its chip (and its identity) even when its row is not in the
 *    currently loaded window — the same objects-not-indices rule the retired
 *    wizard's scope selection followed. Indices break the moment a second page is
 *    appended.
 *
 * 3. **Search is a SERVER re-query** (CONTRACT §7.1), debounced, and changing it
 *    starts the paging again from page 1 — which happens by construction here,
 *    because the query text is part of the react-query key, so a new search is a
 *    new cache entry rather than a list that has to be reset by hand. Never
 *    filter the pages already loaded: matching users living on an unfetched page
 *    would silently disappear.
 */
export const BulkUserPickerModal = ({ isOpen, onClose, onNext }: BulkUserPickerModalProps) => {
	const [selectedUsers, setSelectedUsers] = useState<UserAssignment[]>([]);
	const [query, setQuery] = useState("");
	const [committedQuery, setCommittedQuery] = useState("");

	/**
	 * PRO-757 A4 — a fresh open starts empty. The selection used to be cleared only by this
	 * modal's OWN Cancel (`handleClose`), but the normal path out of here is Next, and
	 * `UserListPage.handleBulkPickerNext` closes the modal with `closeBulkUserPicker()` — not
	 * `handleClose`. Reopening "Allocate Roles" therefore showed the PREVIOUS run's users
	 * still checked and chipped, one unnoticed Next away from granting a role to people the
	 * admin never picked. Resetting on the open transition (rather than asking every caller to
	 * close a particular way) matches `AddAccessFlow`'s and `ManageRolesModal`'s existing
	 * discipline: the dialog owns its own starting state.
	 */
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		setSelectedUsers([]);
		setQuery("");
		setCommittedQuery("");
	}, [isOpen]);

	// Debounced, so a typed word costs one request instead of one per keystroke.
	// `committedQuery` is the only thing the query key sees, which is also what
	// resets paging: a new search is a new cache entry starting at page 1, so there
	// is no accumulated list to unwind by hand.
	useEffect(() => {
		const timer = setTimeout(
			() => setCommittedQuery(query.trim()),
			BULK_USER_PICKER_SEARCH_DEBOUNCE_MS
		);

		return () => clearTimeout(timer);
	}, [query]);

	const {
		data,
		isLoading,
		error,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		refetch,
	} = useInfiniteQuery<UserPage>({
		queryKey: [BULK_USER_PICKER_KEY, BULK_USER_PICKER_PAGE_SIZE, committedQuery],
		queryFn: (context) =>
			getUserAssignmentsPage({
				page: (context.pageParam as number) ?? 1,
				perPage: BULK_USER_PICKER_PAGE_SIZE,
				// Empty means "no filter" — the fetcher drops the param entirely rather
				// than sending `q=`.
				q: committedQuery || undefined,
			}),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.currentPage < lastPage.lastPage ? lastPage.currentPage + 1 : undefined,
		// Opening the modal is what starts the first request — mounting it closed must not.
		enabled: isOpen,
	});

	const pages = useMemo(() => data?.pages ?? [], [data]);

	/**
	 * Every page loaded so far, in order, de-duplicated by id: offset pagination
	 * can repeat a row if the underlying list changes between pages, and a repeat
	 * would otherwise mean duplicate React keys and a user who can be "selected"
	 * twice.
	 */
	const users = useMemo(() => {
		const byId = new Map<number, UserAssignment>();
		pages.forEach((page) => {
			page.data.forEach((user) => {
				if (!byId.has(user.id)) {
					byId.set(user.id, user);
				}
			});
		});
		return Array.from(byId.values());
	}, [pages]);

	const total = pages.length > 0 ? pages[pages.length - 1].total : 0;

	const selectedIds = useMemo(() => selectedUsers.map((user) => user.id), [selectedUsers]);

	const loadedSelectedCount = users.filter((user) => selectedIds.includes(user.id)).length;
	const allLoadedSelected = users.length > 0 && loadedSelectedCount === users.length;
	const someLoadedSelected = loadedSelectedCount > 0 && !allLoadedSelected;

	const toggleUser = (user: UserAssignment) => {
		setSelectedUsers((current) =>
			current.some((selected) => selected.id === user.id)
				? current.filter((selected) => selected.id !== user.id)
				: [...current, user]
		);
	};

	const removeUser = (userId: number) => {
		setSelectedUsers((current) => current.filter((selected) => selected.id !== userId));
	};

	// Select-all can only honestly mean "everyone loaded so far" — the rest of the
	// company has not been fetched. The label says so rather than implying it
	// selected all `total` users.
	const toggleAllLoaded = () => {
		setSelectedUsers((current) => {
			if (allLoadedSelected) {
				return current.filter((selected) => !users.some((user) => user.id === selected.id));
			}
			const additions = users.filter(
				(user) => !current.some((selected) => selected.id === user.id)
			);
			return [...current, ...additions];
		});
	};

	const handleClose = () => {
		setSelectedUsers([]);
		setQuery("");
		setCommittedQuery("");
		onClose();
	};

	// Deliberately does not close: whether the picker stays mounted behind the Add
	// flow (so "< Back" can return to it) is the caller's decision, not this
	// component's.
	const handleNext = () => {
		if (selectedIds.length === 0) {
			return;
		}
		onNext(selectedIds, selectedUsers);
	};

	const footer = (
		<div className="flex w-full items-center justify-between">
			<span className="text-sm text-gray-600" data-testid="bulk-user-picker-count">
				{selectedUsers.length} selected
			</span>
			<div className="flex gap-2">
				<Button
					btntype="plain-border"
					size="sm"
					onClick={handleClose}
					data-testid="bulk-user-picker-cancel"
				>
					Cancel
				</Button>
				<Button
					btntype="primary"
					size="sm"
					disabled={selectedUsers.length === 0}
					onClick={handleNext}
					data-testid="bulk-user-picker-next"
				>
					Next
					<FiArrowRight className="ml-1.5 inline" size={14} />
				</Button>
			</div>
		</div>
	);

	const renderList = () => {
		if (isLoading) {
			return (
				<div
					className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-gray-500"
					data-testid="bulk-user-picker-loading"
				>
					<Spinner size="sm" />
					Loading users…
				</div>
			);
		}

		if (error) {
			return (
				<div
					role="alert"
					className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700"
					data-testid="bulk-user-picker-error"
				>
					<p className="mb-2">Users could not be loaded.</p>
					<Button
						btntype="plain-border"
						size="xs"
						onClick={() => refetch()}
						data-testid="bulk-user-picker-retry"
					>
						Try again
					</Button>
				</div>
			);
		}

		if (users.length === 0) {
			return (
				<p
					className="px-3 py-8 text-center text-sm text-gray-500"
					data-testid="bulk-user-picker-empty"
				>
					{committedQuery ? `No users match “${committedQuery}”.` : "No users found."}
				</p>
			);
		}

		return (
			<>
				<div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
					<Checkbox
						isChecked={allLoadedSelected}
						isIndeterminate={someLoadedSelected}
						onChange={toggleAllLoaded}
						data-testid="bulk-user-picker-select-all"
					>
						<span className="text-sm text-gray-600">
							{`${loadedSelectedCount} of ${users.length} loaded selected`}
							{total > users.length ? ` (${total} in total)` : ""}
						</span>
					</Checkbox>
				</div>

				<div
					className="rounded-md border border-gray-200"
					role="group"
					aria-label="Users"
					data-testid="bulk-user-picker-list"
				>
					{users.map((user) => {
						// A9 — one resolution per row, used by the label, the avatar and the visible
						// name alike. `user.name` is never read directly here.
						const displayName = userDisplayName(user);
						return (
							<div
								key={user.id}
								className="flex items-center gap-2.5 border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-blue-50"
								data-testid={`bulk-user-picker-row-${user.id}`}
							>
								<Checkbox
									isChecked={selectedIds.includes(user.id)}
									onChange={() => toggleUser(user)}
									aria-label={`Select ${displayName}`}
								/>
								<span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[10.5px] font-semibold text-blue-700">
									{initials(displayName)}
								</span>
								<span className="text-sm font-medium">{displayName}</span>
								<span className="text-xs text-gray-500">{user.email}</span>
							</div>
						);
					})}
				</div>

				{hasNextPage && (
					<div className="mt-2 flex justify-center">
						<Button
							btntype="plain-border"
							size="xs"
							disabled={isFetchingNextPage}
							isLoading={isFetchingNextPage}
							onClick={() => fetchNextPage()}
							data-testid="bulk-user-picker-load-more"
						>
							{`Load more (${users.length} of ${total})`}
						</Button>
					</div>
				)}
			</>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Select users"
			description="Choose the people who should get the same access, then continue."
			size="xl"
			modalType="standard"
			iconType="edit"
			// Escape does exactly what this dialog's own Cancel does, and is disabled
			// exactly when Cancel is (gap review F8). Cancel here is always available,
			// so Escape always is: losing a selection to Escape is no worse than
			// losing it to the button sitting next to it, and a dialog that traps
			// focus while ignoring Escape is the worse failure.
			closeOnEsc
			customFooter={footer}
		>
			<div data-testid="bulk-user-picker-modal">
				{/*
					Outside renderList() on purpose: the list area swaps to a spinner while a
					new search resolves, and an input that unmounts mid-query loses focus and
					drops the next keystroke.
				*/}
				<div className="relative mb-2">
					<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
					<CompactTextInput
						type="text"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Search users by name or email…"
						inputClassName="pl-9"
						aria-label="Search users by name or email"
						data-testid="bulk-user-picker-search"
					/>
				</div>

				{renderList()}

				{selectedUsers.length > 0 && (
					<div className="mt-3 flex flex-wrap gap-2" data-testid="bulk-user-picker-chips">
						{selectedUsers.map((user) => {
							// A9 — same rule as the rows above: the chip and its remove button are
							// labelled from the resolved display name, never `user.name` raw.
							const displayName = userDisplayName(user);
							return (
								<span
									key={user.id}
									className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700"
								>
									{displayName}
									<button
										type="button"
										onClick={() => removeUser(user.id)}
										aria-label={`Remove ${displayName}`}
										data-testid={`bulk-user-picker-chip-remove-${user.id}`}
										className="opacity-70 hover:opacity-100"
									>
										<FiX size={12} />
									</button>
								</span>
							);
						})}
					</div>
				)}
			</div>
		</Modal>
	);
};
